// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { parse, stringify } from 'query-string';
import compose from 'recompose/compose';
import {
  withStyles,
  Divider,
  IconButton,
  Typography,
} from '@material-ui/core';
import IconAdd from '@material-ui/icons/Add';
import { get, isEmpty, lowerCase, upperCase } from 'lodash';
import { object, arrayOf, bool, func, string } from 'prop-types';

import {
  getDocumentationTree,
  getNewDocument,
  getSelectedViewDocument,
  getIsDocSaveSuccess,
  getIsDocDeleteSuccess,
  getIsUpdateTreeSuccess,
  getDocumentErrors,
  getIsLoading,
} from '../../reducers/documentation';
import {
  fetchDocumentTree,
  fetchDocument,
  addNewDocument,
  removeNewDocument,
  saveDocument,
  deleteDocument,
  updateDocumentTree,
  resetDocumentNotification,
} from '../../actions/documentation';
import {
  getFirstDocument,
} from '../../components/TreeViewNav/tree';
import { getI18n } from '../../utils/intl';
import {
  TreeViewNav,
  AlertDialog,
  Loading,
  ErrorContainer,
  AlertMessages,
} from '../../components';
import { ALERT_SUCCESS } from '../../constants';
import DocumentView from './DocumentView';
import DocumentEdit from './DocumentEdit';
import DocumentCreate, { createNewDocument } from './DocumentCreate';
import { moveDocument } from './useUpdateDocumentTree';
import styles from './styles';
import { ProductApiOverview, ProductApiTree } from "./ProductApiDocumentation/";

export const DocumentationRaw = (props) => {
  const {
    classes,
    isLoading,
    tabValue,
    documentErrors,
    entityUuid,
    entityType,
    idPrefix = "entity-details",
    userCanEdit,
    userCanAdd = userCanEdit,
    userCanDelete,
    history,
    newDocument,
    selectedViewDocument,
    isDocSaveSuccess,
    isDocDeleteSuccess,
    isUpdateTreeSuccess,
    docTree=[],
    secondaryEntityType='',
  } = props;

  const [selectedEntityUuid, setSelectedEntityUuid] = useState(entityUuid);
  const [selectedEntityType, setSelectedEntityType] = useState(entityType);
  // entity to docTree mapping
  const [entityDocMap, setEntityDocMap] = useState([
    { entityUuid : selectedEntityUuid, entityType : selectedEntityType, docTree : [] },
  ]);
  const [items, setItems] = useState([]); //stores the docTree for a selected entity.
  const [showEntityOverview, setShowEntityOverview] = useState(false);

  const locale = 'en-US';
  const title = 'New-document';
  const intl = getI18n(useIntl());
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [expanded, setExpanded] = useState([docTree.map(obj => obj.id)]);
  const [viewDocument, setViewDocument] = useState(selectedViewDocument);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const emptyFunction = () => {};

  // fetch the doc tree for the selected entity
  useEffect(() => {
    if (tabValue === idPrefix + '-documentation-tab' && selectedEntityUuid && selectedEntityType) {
      const entityExists = entityDocMap.find(e => e.entityUuid === selectedEntityUuid)
      // prevent fetching docTree if tree already exists for an entity.
      if (!entityExists || isEmpty(entityExists.docTree)) {
        props.fetchDocumentTree({
          entityUuid: selectedEntityUuid, entityType: selectedEntityType, locale,
        });
      } else {
        // update the items based on selectedEntityUuid if docTree does not need to update.
        updateItems();
      }
    }
  }, [selectedEntityUuid, tabValue]);

  // update the entityDocMap based on fetched doc tree.
  useEffect(() => {
    const temp = entityDocMap.filter(s => s.entityUuid !== selectedEntityUuid);
    setEntityDocMap([...temp,
      { entityType : selectedEntityType, entityUuid : selectedEntityUuid, docTree : docTree },
    ])
  }, [docTree])

  // update the items if entityDocMap updates.
  useEffect(() => {
    updateItems();
  }, [entityDocMap])

  const updateItems = () => {
    const entity = entityDocMap.find(
      entity => entity.entityType === selectedEntityType &&
        entity.entityUuid === selectedEntityUuid,
    );
    setItems(get(entity, 'docTree', []))
  }

  useEffect(() => {
    if(tabValue === idPrefix + '-documentation-tab') {
      const query = useQuery(location);
      const navtitle = get(query, 'uri', null);
      if(items.length > 0 ) {
        const firstDoc = items.filter(doc =>
          !doc.parentUuid).sort((a, b) => a.ordinal - b.ordinal)[0];
        const currentDoc = items.filter(doc => doc.navtitle === navtitle)[0];
        if (!currentDoc && firstDoc) {
          props.fetchDocument({
            entityUuid : selectedEntityUuid, entityType : selectedEntityType,
            locale, navtitle: firstDoc.navtitle,
          });
        } else if (currentDoc) {
          props.fetchDocument({
            entityUuid : selectedEntityUuid, entityType : selectedEntityType,
            locale, navtitle: currentDoc.navtitle,
          });
        }
      }
    }
  }, [tabValue, items]);

  useEffect(() => {
    setViewDocument(selectedViewDocument);
  }, [selectedViewDocument])

  useEffect(() => {
    setExpanded(items.map(obj => obj.id));
  }, [items]);

  const notifyMessages = (message, status) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const {
      mode,
      selectedDocument,
      selectedDocumentId,
      openDocumentPage,
      openNewDocumentPage,
      closeDocumentPage,
  } = useDocumentationHistory(items, entityUuid, history, props.location, showEntityOverview, tabValue === idPrefix + '-documentation-tab');

  useEffect(() => {
    if (mode === 'add') {
        if (!history.location.state) {
          history.goBack();
          return;
        }
      props.addNewDocument(history.location.state);
    } else if (newDocument) {
      props.removeNewDocument();
    }
}, [history, mode, newDocument]);

useEffect(() => {
  if(isDocSaveSuccess) {
    props.fetchDocumentTree({ entityUuid, entityType, locale });
    const msg = newDocument ? 'Document successfully created' :
    'Document successfully updated';
    notifyMessages(msg, ALERT_SUCCESS);
    setTimeout(() => { props.resetDocumentNotification()}, 2500);
    setTimeout(() => { notifyMessages('', '')}, 2500);
    openDocumentPage(selectedDocument, 'view');
  }
}, [isDocSaveSuccess])

useEffect(() => {
  if(isDocDeleteSuccess || isUpdateTreeSuccess) {
    if(isUpdateTreeSuccess) {
      notifyMessages('Document tree updated successfully', ALERT_SUCCESS);
    }
    if(isDocDeleteSuccess) {
      notifyMessages('Document successfully deleted', ALERT_SUCCESS);
    }
    setTimeout(() => { props.resetDocumentNotification()}, 2500);
    setTimeout(() => { notifyMessages('', '')}, 2500);
    props.fetchDocumentTree({ entityUuid, entityType, locale });
    closeDocumentPage();
  }
}, [isDocDeleteSuccess, isUpdateTreeSuccess])

  const handleDocumentParentChange = ({ documentUuid, newParentUuid, ordinal }) => {

      const newDocuments = moveDocument({
        documentUuid,
        newParentUuid,
        ordinal,
        allDocuments: items,
    });

    const prepareDataForUpdate = items =>
      items.map(({ ...item }) => item);
    const data = prepareDataForUpdate(newDocuments);
    props.updateDocumentTree(entityUuid, entityType, locale, data);
  };

  const handleAddNewDocument = (parentDocument) => {
    if (newDocument !== null) {
      return;
    }

    openNewDocumentPage(
      createNewDocument(
          title,
          parentDocument,
          items,
          entityType,
          entityUuid,
          locale,
      ),
    );

    onSelectEntity({ uuid : entityUuid, type : entityType });
    setShowEntityOverview(false);
  };

  const handleAddNewChildDocument = () => {
    if (newDocument !== null) {
      return;
    }
    openNewDocumentPage(
      createNewDocument(
          title,
          viewDocument,
          items,
          entityType,
          entityUuid,
          locale,
      ),
    );
  };

  const handleSaveNewDocument = (newDoc) => {
    props.saveDocument(newDoc, entityUuid, entityType);
  };

  const handleCancelAddNewDocument = () => {
    closeDocumentPage();
  };

  const handleSelectDocument = document => {
    setShowEntityOverview(false);
    const { typeUuid, type } = document;
    if(selectedEntityUuid !== typeUuid) {
      onSelectEntity({ uuid : typeUuid, type  : lowerCase(type) })
    } else {
      props.fetchDocument({
        entityUuid : document.typeUuid,
        entityType : lowerCase(document.type),
        locale,
        navtitle: document.navtitle,
      });
    }
    openDocumentPage(document, 'view');
  };

const handleEditDocument = () => {
    openDocumentPage(selectedDocument, 'edit');
};

const handleSaveEditDocument = (newDoc) => {
  props.saveDocument(newDoc, entityUuid, entityType);
};

const handleCancelEditDocument = () => {
    openDocumentPage(selectedDocument, 'view');
};

const handleDeleteDocument = () => {
  setShowDeleteDialog(true);
};

const onDelete = () => {
  setShowDeleteDialog(false);
  props.deleteDocument(selectedViewDocument, entityUuid, entityType);
};

const closeDeleteDialog = () => {
  setShowDeleteDialog(false);
}

const hasChildren = () => {
  openDocumentPage(selectedDocument, 'view');
};

  const onSelectEntity = ({ type, uuid }) => {
    if(selectedEntityUuid !== uuid) {
      setSelectedEntityUuid(uuid);
    }
    if(selectedEntityType !== type) {
      setSelectedEntityType(type);
    }
  }

  const getTreeSectionItems = ({ type, uuid }) => {
    const entity = entityDocMap.find(s => s.entityType === type && s.entityUuid === uuid);
    return get(entity, 'docTree',[])
  }

  return (
    <div
      id={idPrefix + "-doc-container"}
      data-apim-test={idPrefix + "-doc-container"}
      className={classes.pageContainer}
    >
      <AlertDialog
          id={idPrefix + "-doc-delete-dialog"}
          data-apim-test={idPrefix + "-doc-delete-dialog"}
          isOpen={showDeleteDialog}
          title={intl.getI18nMessage('label.delete.confirmation.title')}
          description={intl.getI18nMessage('label.entity.details.doc.delete.confirmation.text')}
          submitText={intl.getI18nMessage('label.delete.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          onClose={closeDeleteDialog}
          onSubmit={onDelete}
          onCancel={closeDeleteDialog}
          submitButtonClass={classes.submitButton}
        />
      <div className={classes.treeContainer}>
        <div className={classes.treeToolbar}>
          <Typography variant='h5' className={classes.sectionTitle}>
            {intl.getI18nMessage("label.documentation.section.title", { entityType : upperCase(entityType) })}
          </Typography>
          {userCanAdd && (
              <IconButton
                  className={classes.addRootDocumentationButton}
                  onClick={() => handleAddNewDocument()}
                  disabled={newDocument !== null}
                  aria-label={'New root document'}
                  title={'New root document'}
                  id={idPrefix + "-root-doc-add"}
                  data-apim-test={idPrefix + "-root-doc-add"}
              >
                  <IconAdd />
              </IconButton>
          )}
        </div>
          <Divider />
          <TreeViewNav
              className={classes.tree}
              items={[
                  ...(newDocument !== null ? [newDocument] : []),
                  ...(getTreeSectionItems({ type : entityType, uuid : entityUuid })),
              ]}
              onDocumentSelected={handleSelectDocument}
              selectedDocumentId={
                  newDocument !== null
                      ? newDocument.id
                      : selectedDocumentId
              }
              expanded={expanded}
              onExpandedChange={setExpanded}
              onDocumentParentChange={handleDocumentParentChange}
              canDrag={userCanEdit}
              id={idPrefix + "-doc-tree-view"}
          />
          {
            secondaryEntityType && secondaryEntityType === 'product-api' &&
            <ProductApiTree
              {...props}
              onSelectEntity={onSelectEntity}
              getTreeSectionItems={getTreeSectionItems}
              handleSelectDocument={handleSelectDocument}
              selectedDocumentId={selectedDocumentId}
              expanded={expanded}
              setExpanded={setExpanded}
              userCanEdit={false}
              handleDocumentParentChange={emptyFunction}
              idPrefix={idPrefix}
              tabValue={tabValue}
              parentEntityUuid={entityUuid}
              setShowEntityOverview={setShowEntityOverview}
              isDocTreeLoading={isLoading}
            />
          }
      </div>
      <div className={classes.documentation}>
          {isLoading ?
            <Loading />
          :
            <div>
              {documentErrors.length > 0 &&
                <ErrorContainer errors={documentErrors} />
              }
              {notificationMessage &&
                <AlertMessages
                  className={classes.alertMessages}
                  id={idPrefix + '-doc-message'}
                  variant={notificationStatus}
                  message={notificationMessage}
                  onClose={() => {
                    notifyMessages('', '');
                  }}
                />
              }
              {
                secondaryEntityType === "product-api" && selectedEntityType === 'api' && showEntityOverview &&
                  <ProductApiOverview selectedEntityUuid={selectedEntityUuid}/>
              }
              {selectedDocument && mode === 'view' && !showEntityOverview ? (
                  <DocumentView
                      document={selectedDocument}
                      entityType={entityType}
                      entityUuid={entityUuid}
                      userCanDelete={userCanDelete}
                      userCanEdit={userCanEdit}
                      userCanAdd={userCanAdd}
                      hasChildren={hasChildren}
                      onEdit={handleEditDocument}
                      onAddNewDocument={handleAddNewChildDocument}
                      onDeleteDocument={handleDeleteDocument}
                      allDocuments={items}
                      data={viewDocument}
                      id={idPrefix + "-doc-view"}
                      idPrefix={idPrefix}
                      hideToolbar={secondaryEntityType === "product-api" && selectedEntityType === 'api'}
                  />
              ) : null }
              {selectedDocument && mode === 'edit' && !showEntityOverview ? (
                  <DocumentEdit
                      document={selectedDocument}
                      entityType={entityType}
                      entityUuid={entityUuid}
                      userCanDelete={userCanDelete}
                      userCanEdit={userCanEdit}
                      hasChildren={hasChildren}
                      onSave={handleSaveEditDocument}
                      onCancel={handleCancelEditDocument}
                      onAddNewDocument={handleAddNewDocument}
                      onDeleteDocument={handleDeleteDocument}
                      selectedViewDocument={selectedViewDocument}
                      allDocuments={items}
                      id={idPrefix + "-doc-edit"}
                      idPrefix={idPrefix}
                      hideToolbar={secondaryEntityType === "product-api" && selectedEntityType === 'api'}
                  />
              ) : null }
              {newDocument && mode === 'add' && !showEntityOverview ? (
                  <DocumentCreate
                      document={newDocument}
                      entityType={entityType}
                      entityUuid={entityUuid}
                      allDocuments={items}
                      onSave={handleSaveNewDocument}
                      onCancel={handleCancelAddNewDocument}
                      id={idPrefix + "-doc-create"}
                      idPrefix={idPrefix}
                  />
              ) : null}
            </div>
          }

      </div>
    </div>
  );
}

function useQuery(location) {
  return parse(location.search);
}

function useDocumentationHistory(items, entityUuid, history, location,
   showEntityOverview, isDocTab) {
  const query = useQuery(location);
  const selectedDocumentNavtitle = get(query, 'uri', null);
  const mode = get(query, 'mode', 'view');

  const selectedDocument =
      items.find(
          documentation => documentation.navtitle === selectedDocumentNavtitle,
      ) || null;
  const selectedDocumentId = selectedDocument ? selectedDocument.id : null;
  const firstDocument = useMemo(() => getFirstDocument(items), [items]);

  const openDocumentPage = useCallback(
        (document = null, mode = 'view', state = null) => {
            const search = history.location.pathname.includes('/documentation') ? stringify({
              ...(document && { uri: document.navtitle }),
              mode }) : '';

            return history.push({
                pathname: `${history.location.pathname}`,
                search,
                ...(state !== null && { state }),
            });
        },
        [history],
    );
  const openNewDocumentPage = state => {
      return openDocumentPage(null, 'add', state);
  };

  const closeDocumentPage = () => {
      return history.push({
          pathname: history.location.pathname,
      });
  };

  useEffect(() => {
      if (
          mode !== 'add' &&
          selectedDocumentNavtitle === null &&
          firstDocument !== null &&
          !showEntityOverview && // prevent opening doc page when user click on entity overview.
          isDocTab // when switched to doc tab, first doc needs to be opened.
      ) {
          openDocumentPage(firstDocument, 'view');
      }
  }, [
      firstDocument,
      items,
      mode,
      openDocumentPage,
      selectedDocument,
      selectedDocumentNavtitle,
      isDocTab,
  ]);

  return {
      // If a document was requested but not found
      error:
          selectedDocumentNavtitle && !selectedDocumentId
              ? 'resources.documents.notifications.not_found'
              : undefined,
      mode,
      selectedDocument,
      selectedDocumentId,
      openDocumentPage,
      openNewDocumentPage,
      closeDocumentPage,
  };
}

DocumentationRaw.propTypes = {
  classes: object,
  userContext: object,
  isLoading: bool,
}

const mapStateToProps = state => ({
    docTree : getDocumentationTree(state),
    newDocument: getNewDocument(state),
    selectedViewDocument: getSelectedViewDocument(state),
    isDocSaveSuccess: getIsDocSaveSuccess(state),
    isDocDeleteSuccess: getIsDocDeleteSuccess(state),
    isUpdateTreeSuccess: getIsUpdateTreeSuccess(state),
    documentErrors:getDocumentErrors(state),
    isLoading : getIsLoading(state),
});

const mapDispatchToProps = {
  fetchDocumentTree,
  fetchDocument,
  addNewDocument,
  removeNewDocument,
  saveDocument,
  deleteDocument,
  updateDocumentTree,
  resetDocumentNotification,
};

DocumentationRaw.displayName = 'Documentation';

DocumentationRaw.propTypes = {
  classes: object,
  isLoading: bool,
  tabValue: string,
  documentErrors: arrayOf(object),
  entityUuid: string,
  entityType: string,
  idPrefix: string,
  docTree: arrayOf(object),
  userCanEdit: bool,
  userCanDelete: bool,
  userCanAdd: bool,
  history: object,
  newDocument: object,
  selectedViewDocument: object,
  location: object,
  isDocSaveSuccess: bool,
  isDocDeleteSuccess: bool,
  isUpdateTreeSuccess: bool,
  fetchDocumentTree: func,
  fetchDocument: func,
  addNewDocument: func,
  removeNewDocument: func,
  saveDocument: func,
  deleteDocument: func,
  updateDocumentTree: func,
  resetDocumentNotification: func,
  secondaryEntityType : string,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(DocumentationRaw);
